import type { ReactNode } from "react"
import { useEffect, useState } from "react"
import {
	IconChevronMinimizeDiagonalOutline18 as Minimize,
	IconMicrophoneOutline18 as Mic,
	IconMicrophoneSlashOutline18 as MicOff,
	IconPhoneOutline18 as Phone,
	IconPaperPlane2Outline18 as Send,
	IconXmarkOutline18 as Close,
} from "nucleo-ui-outline-18"

import type { AgentOrbState } from "@productlane/ui/src/components/agent-orb/agent-orb"
import { cn } from "@productlane/lib/src/cn"
import {
	AgentOrb,
	agentOrbColorsFromAccent,
} from "@productlane/ui/src/components/agent-orb/agent-orb"

import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"
import { DesignIssueHeader } from "./design-issue-header"

/* ------------------------------------------------------------------ */
/* Geometry: one customer site per artboard, widget in its corner      */
/* ------------------------------------------------------------------ */

const FRAME_W = 1100
const FRAME_H = 680
const INSET = 24
const PANEL_W = 380
const PANEL_H = 560
const PANEL_LEFT = FRAME_W - INSET - PANEL_W
const PANEL_TOP = FRAME_H - INSET - PANEL_H
const ORB_COLORS = agentOrbColorsFromAccent("#49AE7C")
// The orb renders once at its large size and flies between slots on a transform.
const ORB_RENDER = 200
const MORPH = "cubic-bezier(0.32, 0.72, 0, 1)"

export type VoiceVariant = "orb" | "transcript" | "spotlight"
export type VoiceState = "closed" | "open" | "minimized"

interface Slot {
	left: number
	top: number
	size: number
}

interface VariantSpec {
	pillWidth: number
	pillHeight: number
	pillOrb: Slot
	openOrb: Slot
}

const SPECS: Record<VoiceVariant, VariantSpec> = {
	orb: {
		pillWidth: 210,
		pillHeight: 60,
		pillOrb: {
			left: FRAME_W - INSET - 210 + 10,
			top: FRAME_H - INSET - 50,
			size: 40,
		},
		openOrb: { left: PANEL_LEFT + 90, top: PANEL_TOP + 100, size: 200 },
	},
	transcript: {
		pillWidth: 64,
		pillHeight: 64,
		pillOrb: {
			left: FRAME_W - INSET - 56,
			top: FRAME_H - INSET - 56,
			size: 48,
		},
		openOrb: { left: PANEL_LEFT + 24, top: PANEL_TOP + 20, size: 56 },
	},
	spotlight: {
		pillWidth: 225,
		pillHeight: 48,
		pillOrb: {
			left: FRAME_W - INSET - 225 + 11,
			top: FRAME_H - INSET - 37,
			size: 26,
		},
		openOrb: { left: PANEL_LEFT + 115, top: PANEL_TOP + 118, size: 150 },
	},
}

/** Cycles the agent between listening and speaking, as a call would. */
function useCallState(active: boolean): AgentOrbState {
	const [speaking, setSpeaking] = useState(true)
	useEffect(() => {
		if (!active) return
		const interval = setInterval(() => setSpeaking((s) => !s), 3200)
		return () => clearInterval(interval)
	}, [active])
	if (!active) return "idle"
	return speaking ? "talking" : "listening"
}

/* ------------------------------------------------------------------ */
/* The customer site behind the widget                                 */
/* ------------------------------------------------------------------ */

function CustomerSite({ page }: { page: "pricing" | "docs" }) {
	return (
		<div className="absolute inset-0 bg-white">
			<div className="flex h-14 items-center justify-between border-b border-gray-200 px-8">
				<div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
					<span className="size-6 rounded-md bg-gray-900" />
					Acme
				</div>
				<div className="flex gap-6 text-sm text-gray-500">
					<span className={cn(page === "pricing" && "text-gray-900")}>
						Pricing
					</span>
					<span className={cn(page === "docs" && "text-gray-900")}>Docs</span>
					<span>Changelog</span>
				</div>
			</div>
			<div className="space-y-4 px-16 py-14">
				<div className="text-3xl font-semibold tracking-tight text-gray-900">
					{page === "pricing" ? "Simple pricing for every team" : "Exports"}
				</div>
				<div className="h-3 w-2/3 rounded bg-gray-100" />
				<div className="h-3 w-1/2 rounded bg-gray-100" />
				<div className="grid grid-cols-3 gap-4 pt-6">
					{[0, 1, 2].map((i) => (
						<div
							key={i}
							className="h-40 rounded-xl bg-gray-50 ring-1 ring-gray-200"
						/>
					))}
				</div>
			</div>
		</div>
	)
}

/* ------------------------------------------------------------------ */
/* Pills (closed and minimized)                                        */
/* ------------------------------------------------------------------ */

function Pill({
	variant,
	state,
	orbState,
	onOpen,
	onEnd,
}: {
	variant: VoiceVariant
	state: VoiceState
	orbState: AgentOrbState
	onOpen: () => void
	onEnd: () => void
}) {
	const spec = SPECS[variant]
	const inCall = state === "minimized"
	const status = orbState === "talking" ? "Speaking…" : "Listening…"
	const shown = state !== "open"
	return (
		<div
			className="absolute flex items-center rounded-full bg-white shadow-[0_2px_6px_rgba(0,0,0,0.06),0_12px_28px_-8px_rgba(0,0,0,0.22)] ring-1 ring-black/[0.08]"
			style={{
				right: INSET,
				bottom: INSET,
				width: spec.pillWidth,
				height: spec.pillHeight,
				opacity: shown ? 1 : 0,
				pointerEvents: shown ? "auto" : "none",
				transition: `opacity 200ms ${MORPH}`,
			}}
		>
			<button
				type="button"
				onClick={onOpen}
				className="flex h-full min-w-0 flex-1 items-center rounded-full text-left"
				style={{
					paddingLeft: variant === "transcript" ? 0 : spec.pillHeight - 6,
				}}
			>
				{variant === "orb" && (
					<span className="min-w-0 pl-3">
						<span className="block text-[15px] font-medium text-gray-900">
							Voice chat
						</span>
						<span className="block text-xs text-[#3b82f6]">
							{inCall ? status : "Talk to Acme"}
						</span>
					</span>
				)}
				{variant === "spotlight" && (
					<span className="truncate pl-1 text-sm text-gray-500">
						{inCall ? status : "Ask anything"}
					</span>
				)}
			</button>
			{inCall && variant !== "transcript" && (
				<button
					type="button"
					onClick={onEnd}
					aria-label="End call"
					className="mr-2 flex size-8 flex-none items-center justify-center rounded-full bg-red-500 text-white"
				>
					<Phone className="size-4 flex-none rotate-[135deg]" />
				</button>
			)}
			{inCall && variant === "transcript" && (
				<button
					type="button"
					onClick={onEnd}
					aria-label="End call"
					className="absolute -right-1 -top-1 flex size-5 items-center justify-center rounded-full bg-gray-900 text-white ring-2 ring-white"
				>
					<Close className="size-3 flex-none" />
				</button>
			)}
		</div>
	)
}

/* ------------------------------------------------------------------ */
/* Open panels                                                         */
/* ------------------------------------------------------------------ */

function PanelShell({
	open,
	onMinimize,
	children,
}: {
	open: boolean
	onMinimize: () => void
	children: ReactNode
}) {
	return (
		<div
			className="absolute flex flex-col overflow-hidden rounded-[28px] bg-white shadow-[0_8px_24px_rgba(0,0,0,0.12),0_24px_60px_-12px_rgba(0,0,0,0.25)] ring-1 ring-black/[0.08]"
			style={{
				left: PANEL_LEFT,
				top: PANEL_TOP,
				width: PANEL_W,
				height: PANEL_H,
				transformOrigin: "100% 100%",
				transform: open ? "none" : "translateY(12px) scale(0.92)",
				opacity: open ? 1 : 0,
				pointerEvents: open ? "auto" : "none",
				transition: `transform 450ms ${MORPH}, opacity 250ms ${MORPH}`,
			}}
		>
			<div className="flex items-center justify-end p-4">
				<button
					type="button"
					onClick={onMinimize}
					aria-label="Minimize"
					className="flex size-9 items-center justify-center rounded-full bg-gray-100 text-gray-700"
				>
					<Minimize className="size-4 flex-none" />
				</button>
			</div>
			{children}
		</div>
	)
}

function CallControls({
	muted,
	onMute,
	onEnd,
}: {
	muted: boolean
	onMute: () => void
	onEnd: () => void
}) {
	return (
		<div className="flex items-center justify-center gap-3">
			<button
				type="button"
				onClick={onMute}
				aria-label={muted ? "Unmute" : "Mute"}
				className="flex size-11 items-center justify-center rounded-full bg-gray-100 text-gray-800"
			>
				{muted ? (
					<MicOff className="size-5 flex-none" />
				) : (
					<Mic className="size-5 flex-none" />
				)}
			</button>
			<button
				type="button"
				onClick={onEnd}
				aria-label="End call"
				className="flex size-11 items-center justify-center rounded-full bg-red-500 text-white"
			>
				<Phone className="size-5 flex-none rotate-[135deg]" />
			</button>
		</div>
	)
}

function MessageField() {
	return (
		<div className="flex items-center rounded-full bg-white px-5 py-3 text-sm text-gray-400 ring-1 ring-gray-200">
			<span className="flex-1">Or send a message…</span>
			<Send className="size-4 flex-none text-gray-400" />
		</div>
	)
}

const TRANSCRIPT = [
	{ from: "you", text: "Why does my CSV export stop at 1,000 rows?" },
	{
		from: "agent",
		text: "The export pages at 1,000 rows. I checked your workspace: you have 4,312.",
	},
	{
		from: "agent",
		text: "I filed it with the team and will email you once the fix is live.",
	},
]

function PanelContent({
	variant,
	inCall,
	orbState,
	muted,
	onStart,
	onMute,
	onEnd,
}: {
	variant: VoiceVariant
	inCall: boolean
	orbState: AgentOrbState
	muted: boolean
	onStart: () => void
	onMute: () => void
	onEnd: () => void
}) {
	const status = !inCall
		? "Tap the orb to talk"
		: orbState === "talking"
			? "Speaking…"
			: "Listening…"
	if (variant === "transcript") {
		return (
			<div className="flex min-h-0 flex-1 flex-col px-5 pb-5">
				<div className="-mt-12 mb-4 pl-[72px]">
					<div className="text-[15px] font-semibold text-gray-900">
						Acme Support
					</div>
					<div className="text-xs text-[#3b82f6]">{status}</div>
				</div>
				<div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden pt-2">
					{TRANSCRIPT.map((line, i) => (
						<div
							key={i}
							className={cn(
								"max-w-[85%] rounded-2xl px-3.5 py-2 text-sm",
								line.from === "you"
									? "self-end bg-gray-900 text-white"
									: "self-start bg-gray-100 text-gray-800",
							)}
						>
							{line.text}
						</div>
					))}
				</div>
				<div className="space-y-3 pt-3">
					{inCall ? (
						<CallControls muted={muted} onMute={onMute} onEnd={onEnd} />
					) : (
						<button
							type="button"
							onClick={onStart}
							aria-label="Start voice chat"
							className="mx-auto flex h-11 items-center gap-2 rounded-full bg-gray-900 px-5 text-sm font-medium text-white"
						>
							<Phone className="size-4 flex-none" /> Start voice chat
						</button>
					)}
					<MessageField />
				</div>
			</div>
		)
	}
	return (
		<div className="flex min-h-0 flex-1 flex-col items-center px-6 pb-6">
			{/* Room for the flying orb; the call button sits on top of it. */}
			<div
				className="relative flex flex-none items-center justify-center"
				style={{
					height: SPECS[variant].openOrb.size + (variant === "orb" ? 64 : 100),
					width: "100%",
				}}
			>
				{!inCall && (
					<button
						type="button"
						onClick={onStart}
						aria-label="Start voice chat"
						className="relative z-10 mt-[-10px] flex size-14 items-center justify-center rounded-full bg-white shadow-md"
					>
						<Phone className="size-6 flex-none text-gray-900" />
					</button>
				)}
			</div>
			<div className="text-center text-sm text-gray-500">{status}</div>
			{variant === "spotlight" && inCall && (
				<div className="mt-4 w-full rounded-2xl bg-gray-50 px-4 py-3 text-sm text-gray-700">
					{orbState === "talking" ? TRANSCRIPT[1]!.text : TRANSCRIPT[0]!.text}
				</div>
			)}
			<div className="mt-auto w-full space-y-3">
				{inCall && <CallControls muted={muted} onMute={onMute} onEnd={onEnd} />}
				<MessageField />
			</div>
		</div>
	)
}

/* ------------------------------------------------------------------ */
/* One live artboard                                                   */
/* ------------------------------------------------------------------ */

export function VoiceWidgetDemo({
	variant,
	initialState = "closed",
}: {
	variant: VoiceVariant
	initialState?: VoiceState
}) {
	const [state, setState] = useState<VoiceState>(initialState)
	const [inCall, setInCall] = useState(initialState === "minimized")
	const [muted, setMuted] = useState(false)
	const orbState = useCallState(inCall)
	const spec = SPECS[variant]
	const slot = state === "open" ? spec.openOrb : spec.pillOrb
	const scale = slot.size / ORB_RENDER
	const end = () => {
		setInCall(false)
		setState("closed")
	}

	return (
		<div className="space-y-3">
			<div className="flex gap-1.5">
				{(["closed", "open", "minimized"] as const).map((s) => (
					<button
						key={s}
						type="button"
						onClick={() => {
							if (s === "minimized") setInCall(true)
							if (s === "closed") setInCall(false)
							setState(s)
						}}
						className={cn(
							"rounded-full px-3 py-1 text-xs font-medium ring-1",
							state === s
								? "bg-gray-900 text-white ring-gray-900"
								: "bg-white text-gray-600 ring-gray-200",
						)}
					>
						{s === "closed"
							? "Closed"
							: s === "open"
								? "Open"
								: "Shrunk while browsing"}
					</button>
				))}
			</div>
			<div
				className="relative overflow-hidden rounded-2xl ring-1 ring-black/10"
				style={{ width: FRAME_W, height: FRAME_H }}
			>
				<CustomerSite page={state === "minimized" ? "docs" : "pricing"} />
				<PanelShell
					open={state === "open"}
					onMinimize={() => setState(inCall ? "minimized" : "closed")}
				>
					<PanelContent
						variant={variant}
						inCall={inCall}
						orbState={orbState}
						muted={muted}
						onStart={() => setInCall(true)}
						onMute={() => setMuted((m) => !m)}
						onEnd={end}
					/>
				</PanelShell>
				<Pill
					variant={variant}
					state={state}
					orbState={orbState}
					onOpen={() => setState("open")}
					onEnd={end}
				/>
				{/* The one orb, flying between the pill and the open panel. */}
				<div
					className="pointer-events-none absolute left-0 top-0 z-20"
					data-voice-orb={state}
					style={{
						width: ORB_RENDER,
						height: ORB_RENDER,
						transformOrigin: "0 0",
						transform: `translate(${slot.left}px, ${slot.top}px) scale(${scale})`,
						transition: `transform 480ms ${MORPH}`,
					}}
				>
					<AgentOrb size={ORB_RENDER} state={orbState} colors={ORB_COLORS} />
				</div>
			</div>
		</div>
	)
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const VERSIONS: { label: string; note: string; variant: VoiceVariant }[] = [
	{
		label: "Version A",
		note: "Like the ElevenLabs widget: one big orb with the call button on it. Shrunk, a Voice chat pill keeps the call and shows who is speaking.",
		variant: "orb",
	},
	{
		label: "Version B",
		note: "Transcript first: the orb is the avatar, the conversation reads along as text with cards. Shrunk, only the orb stays, with an end button.",
		variant: "transcript",
	},
	{
		label: "Version C",
		note: "Built on the spotlight bar: the bar opens into a voice panel with the orb and the live caption. Shrunk, the call runs inside the bar.",
		variant: "spotlight",
	},
]

export function VoiceAgentPage() {
	if (!isDevEnvironment) return <NotFound />
	return (
		<div className="min-h-screen space-y-12 bg-gray-50 px-10 py-10 pb-24">
			<div className="max-w-[1200px]">
				<DesignIssueHeader
					identifier="Design"
					title="Talk to the agent in the widget"
					url="http://100.90.47.10:5173/design/voice-agent"
					summary="Three ways for a visitor to talk to the agent by voice on a customer's site. Each version is a live customer page: switch between closed, open and shrunk while browsing, click the pill to open, and use the minimize button to keep talking while the page behind changes. The orb flies between the pill and the open panel. The copy is a placeholder and goes through wording before it ships."
				/>
			</div>
			<div className="flex flex-nowrap items-start gap-10 overflow-x-auto pb-4">
				{VERSIONS.map((version) => (
					<div
						key={version.label}
						className="flex-none space-y-4"
						style={{ width: FRAME_W }}
					>
						<div className="space-y-1">
							<div className="text-base font-semibold text-gray-900">
								{version.label}
							</div>
							<div className="text-sm text-gray-500">{version.note}</div>
						</div>
						<VoiceWidgetDemo variant={version.variant} />
					</div>
				))}
			</div>
		</div>
	)
}
